import { useRef, useEffect } from "react";
import { v4 as uuidv4 } from 'uuid';

export interface TreeNode {
  id?: string;
  name: string;
  type: 'file' | 'folder';
  files?: TreeNode[];
  parent?: TreeNode;
}

interface SearchDFSParams {
  data: TreeNode[];
  cond: (item: TreeNode) => boolean;
}

interface SearchDFSResult {
  item: TreeNode | null;
  parent: TreeNode | null;
  nextSibling: TreeNode | null;
  previousSibling: TreeNode | null;
}

export const findNodeById = (nodes: TreeNode[], id: string): TreeNode | null => {
  for (const n of nodes) {
    if (n.id === id) return n;
    if (n.files) {
      const found = findNodeById(n.files, id);
      if (found) return found;
    }
  }
  return null;
};

export const searchDFS = ({ data, cond }: SearchDFSParams): SearchDFSResult => {
  let final: SearchDFSResult = {
    parent: null,
    item: null,
    nextSibling: null,
    previousSibling: null
  };
  let parentPath: TreeNode[] = [];

  const recursive = (tree: TreeNode[], item: TreeNode) => {
    if (cond(item)) {
      final = {
        parent: parentPath[parentPath.length - 1] || null,
        item,
        nextSibling: null,
        previousSibling: null
      };
      return true;
    }

    if (item.files) {
      parentPath.push(item);
      for (let child of item.files) {
        if (recursive(item.files, child)) return true;
      }
      parentPath.pop();
    }

    return false;
  };

  for (let item of data) {
    if (recursive(data, item)) break;
  }
  return final;
};

export const createFile = ({ name }: { name: string }): TreeNode => ({
  id: uuidv4(),
  name,
  type: 'file'
});

export const createFolder = ({ name }: { name: string }): TreeNode => ({
  id: uuidv4(),
  name,
  type: 'folder',
  files: []
});

export const useDidMountEffect = (func: () => void, deps: any[]) => {
  const didMount = useRef(false);

  useEffect(() => {
    if (didMount.current) func();
    else didMount.current = true;
  }, deps);
};
